import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { animateEnergy } from '../animation/CurrencyAnimation';

type CloseHandler = (result: boolean) => void;

export default class PopupGiftReward extends PopupBasic {
  private rewardLabel: LangBitmapFontTextView;
  private energyIcon: ImageScaleView;
  private energyAmount: number = 0;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      zIndex: 10000,
      width: uiConfig.popups.minimumWidth,
      height: 450,
      tracking: { feature: 'tutorial', $subFeature: 'popup-giftReward' },
      titleIcon: {
        image: 'assets/ui/gifts/icons/gifts_popup_icon.png',
      },
    });

    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      width: 400,
      height: 40,
      x: this.box.style.width / 2,
      y: 100,
      zIndex: 1,
      wordWrap: false,
      centerOnOrigin: true,
    });

    this.box.addSubview(
      new ImageScaleView({
        height: 5,
        width: 400,
        x: this.box.style.width / 2,
        y: 130,
        image: 'assets/ui/popups/panel_underline.png',
        scaleMethod: '3slice',
        sourceSlices: {
          horizontal: { left: 5, right: 5 },
        },
        centerOnOrigin: true,
      }),
    );

    const reward = this.box.addSubview(
      new ImageSliceView({
        image: 'assets/ui/shared/lossless/inset_panel.png',
        slices: {
          left: 25,
          right: 25,
          top: 25,
          bot: 25,
        },
        centerOnOrigin: true,
        x: this.box.style.width / 2,
        y: 200,
        height: 88,
        width: 300,
      }),
    );

    this.energyIcon = reward.addSubview(
      new ImageScaleView({
        scaleMethod: 'contain',
        image: 'assets/ui/hud/icons/hud_energy.png',
        width: 74,
        height: 74,
        x: 10,
        y: 7,
      }),
    );

    this.rewardLabel = new LangBitmapFontTextView({
      size: 38,
      font: 'Body',
      height: 68,
      width: 196,
      verticalAlign: 'center',
      align: 'center',
      x: 90,
      y: 8,
      superview: reward,
    });

    new LangBitmapFontTextView({
      superview: this.box,
      color: '#b68351',
      size: 30,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      font: 'Body',
      width: 350,
      height: 50,
      y: 290,
      wordWrap: true,
      x: this.box.style.width / 2,
      localeText: () => i18n('gifts.popupGiftReward.warning'),
    });

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...uiConfig.buttons.secondary,
        labelOffsetX: -4,
        labelOffsetY: -1,
        fontSize: 38,
        font: bitmapFonts('Body'),
        x: this.box.style.width / 2,
        y: this.box.style.height - 35 - 80 / 2,
        width: 229,
        height: 80,
        centerOnOrigin: true,
        localeText: () => i18n('basic.ok'),
        onClick: async () => {
          sounds.playSound('ok');
          const iconPos = this.energyIcon.getPosition(this.root);
          const start = {
            x: iconPos.x + this.energyIcon.style.width / 2,
            y: iconPos.y + this.energyIcon.style.height / 2,
          };

          await animateEnergy({
            superview: this.root,
            start: { position: start, scale: 1 },
            count: this.energyAmount,
          });
          opts.close(true);
        },
        tracking: {
          feature: 'tutorial',
          $subFeature: 'popup-giftReward-close',
        },
      }),
    );
  }

  public init({ amount }: { amount: number }) {
    super.init({
      title: i18n('gifts.popupGiftReward.title'),
      message: i18n('gifts.popupGiftReward.message'),
    });

    this.energyAmount = amount;
    this.rewardLabel.localeText = () => `x ${parseAmount(this.energyAmount)}`;
  }
}
